import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { TurnSlots } from "../thread/slots";

/** `kv-grid`, in the terminal register — keys padded to a common width in
 * CHARACTER cells, values flush at the next column.
 *
 * This slot is why the terminal skin cannot be CSS applied to the native
 * components. The native grid delegates to `std-property-list`, which aligns
 * with CSS grid: the value column lands wherever the widest key happens to
 * render, in fractional pixels. That is correct typesetting and it is not
 * what a terminal does. A terminal pads with SPACES, so the value column sits
 * on a character boundary and stays aligned when the text is copied out,
 * diffed, or pasted into a shell.
 *
 * So the padding is computed here, in characters, and the row is a single
 * pre-formatted string. Copying a block of these yields exactly what the
 * terminal would have written — which is the property that makes the skin
 * worth having rather than a re-skin. */
@component({ name: "std-term-turn-kv-grid" })
export class TermTurnKvGrid extends LonaWebComponent {
  private $rows: HTMLElement = this.$("rows");

  static makeWith(props: TurnSlots.Props["kv-grid"]): TermTurnKvGrid {
    return (TermTurnKvGrid.make() as TermTurnKvGrid).bind(props);
  }

  bind(props: TurnSlots.Props["kv-grid"]): this {
    // Width is measured in code points, not UTF-16 units: a key containing an
    // astral character would otherwise be counted twice and under-padded.
    const width = props.pairs.reduce((max, [key]) => Math.max(max, [...key].length), 0);
    this.$rows.replaceChildren();
    for (const [key, value] of props.pairs) {
      const $row = document.createElement("div");
      $row.className = "row";
      const $key = document.createElement("span");
      $key.className = "key";
      $key.textContent = key.padEnd(width, " ");
      const $value = document.createElement("span");
      $value.className = "value";
      $value.textContent = value;
      $row.append($key, document.createTextNode("  "), $value);
      this.$rows.append($row);
    }
    return this;
  }

  static $styles = [
    css`
      :host {
        display: block;
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: var(--turn-term-size, 12px);
        line-height: var(--turn-term-leading, 1.7);
      }
      /* pre whitespace is load-bearing: the padding IS the alignment, and
         collapsing it would silently undo the whole point of this component. */
      .row {
        white-space: pre;
        overflow-x: auto;
      }
      .key {
        color: var(--text-ter, #8b93a7);
      }
      .value {
        color: var(--text-pri, #1f2328);
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`<div id="rows"></div>`;
}
